import clsx from 'clsx';
import { Building2, ChevronLeft, ChevronRight } from 'lucide-react';

import type { SidebarProps } from './Sidebar.types';
import { NavSections } from './NavSections';

export const Sidebar = ({
  isOpen,
  isCollapsed,
  sections,
  onToggleCollapse,
  onClose,
}: SidebarProps) => {
  // On mobile, clicking a nav item should close the sidebar
  const handleMobileItemClick = () => {
    // Only close on mobile (when sidebar is in overlay mode)
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay - click to close */}
      {isOpen && (
        <div
          className='fixed inset-0 z-40 bg-black/50 transition-opacity lg:hidden'
          onClick={onClose}
        />
      )}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white transition-all duration-300',
          // Mobile: slide in/out, always full width
          // Desktop: always visible, can be collapsed
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // On mobile always show full width (w-70), on desktop respect collapsed state
          'w-70',
          isCollapsed ? 'lg:w-16' : 'lg:w-62.5'
        )}
      >
        {/* Logo Header */}
        <div
          className={clsx(
            'flex h-16 shrink-0 items-center border-b border-gray-100 px-4',
            isCollapsed ? 'lg:justify-center' : 'gap-3'
          )}
        >
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-200'>
            <Building2 className='h-5 w-5 text-white' />
          </div>
          {/* Show title on mobile always, on desktop only when not collapsed */}
          <div className={clsx('flex flex-col', isCollapsed && 'lg:hidden')}>
            <span className='text-lg font-bold text-gray-900'>Supplier</span>
            <span className='text-xs text-gray-500'>Portal</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className='flex-1 space-y-4 overflow-y-auto p-3'>
          {sections.map((section, index) => (
            <NavSections
              key={section.title ?? index}
              section={section}
              isCollapsed={isCollapsed}
              onItemClick={handleMobileItemClick}
            />
          ))}
        </nav>

        {/* Collapse Toggle Button - Desktop only */}
        <div className='hidden border-t border-gray-100 p-3 lg:block'>
          <button
            onClick={onToggleCollapse}
            className={clsx(
              'group flex w-full items-center justify-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-600 transition-all',
              'hover:bg-gray-100 hover:text-gray-900',
              isCollapsed && 'lg:px-2'
            )}
          >
            {isCollapsed ? (
              <ChevronRight className='h-4 w-4 transition-transform group-hover:translate-x-0.5' />
            ) : (
              <>
                <ChevronLeft className='h-4 w-4 transition-transform group-hover:-translate-x-0.5' />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};
